import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiCheckLine, RiFileCopyLine } from '@remixicon/react'
import Button from './Button'
import Input from './Input'

interface CopyFieldProps {
  value: string
  /** What the value is: names the field and the button for assistive technology. */
  label: string
  className?: string
}

/**
 * A read-only value with a button that copies it. The feedback is announced as
 * well as drawn, a refused copy (an insecure page, a denied permission) is not an
 * unhandled error, and the value is selected on focus so it can be copied by hand.
 */
export default function CopyField({ value, label, className }: CopyFieldProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Nothing to say that the selected value does not already offer: it can be copied by hand.
      setCopied(false)
    }
  }

  return (
    <div className="flex gap-2">
      <Input
        readOnly
        aria-label={label}
        value={value}
        className={className}
        onFocus={(event) => event.currentTarget.select()}
      />
      <Button type="button" variant="secondary" onClick={copy} title={t('common.copy')} className="size-10 shrink-0 p-0">
        {copied ? (
          <RiCheckLine className="size-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        ) : (
          <RiFileCopyLine className="size-4" aria-hidden="true" />
        )}
        <span className="sr-only">{`${t('common.copy')}: ${label}`}</span>
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? t('common.copied') : ''}
      </span>
    </div>
  )
}
